import { FC, useEffect, useRef, ReactNode } from 'react';

export interface MobileTourContent {
  emoji?: string;
  title: string;
  subtitle?: string;
  tipTitle?: string;
  tipBody: ReactNode; // pra permitir <b className="text-accent">...</b>
  ctaLabel?: string;
}

interface MobileWelcomeModalProps {
  content: MobileTourContent;
  onComplete: () => void;
  onSkip: () => void;
}

/**
 * Versão simplificada do tour em mobile.
 * Um único modal de boas-vindas full-width como bottom sheet (mobile)
 * ou centralizado (tablet portrait).
 *
 * Sem spotlights — em telas pequenas, ancorar em elementos pequenos vira UX
 * ruim. O conteúdo é genérico (via prop `content`) — cada tour passa o seu.
 */
const MobileWelcomeModal: FC<MobileWelcomeModalProps> = ({ content, onComplete, onSkip }) => {
  const ctaRef = useRef<HTMLButtonElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  // Focus management: foca no CTA ao abrir, restaura ao fechar.
  useEffect(() => {
    previouslyFocused.current = document.activeElement as HTMLElement;
    ctaRef.current?.focus();
    return () => {
      previouslyFocused.current?.focus?.();
    };
  }, []);

  // Esc pra fechar
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onSkip();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onSkip]);

  // Trava scroll do body enquanto modal aberto
  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, []);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={onSkip}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="mobile-tour-title"
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-md bg-surface-card border-t sm:border border-theme-border rounded-t-3xl sm:rounded-2xl shadow-2xl p-6 space-y-4 animate-in slide-in-from-bottom duration-300"
      >
        {/* Drag handle (visual, não interativo) */}
        <div className="sm:hidden w-12 h-1 bg-content-muted/30 rounded-full mx-auto -mt-2 mb-2" />

        {/* Header */}
        <div className="flex items-start gap-3">
          {content.emoji && (
            <span className="text-3xl shrink-0 leading-none">{content.emoji}</span>
          )}
          <div className="flex-1 min-w-0">
            <h2
              id="mobile-tour-title"
              className="text-xl font-bold text-content-primary leading-tight"
            >
              {content.title}
            </h2>
            {content.subtitle && (
              <p className="text-sm text-content-secondary mt-1">{content.subtitle}</p>
            )}
          </div>
        </div>

        {/* Dica principal */}
        <div className="bg-surface-secondary border border-theme-border rounded-xl p-4 flex items-start gap-3">
          <span className="material-symbols-outlined text-accent shrink-0">tips_and_updates</span>
          <div className="flex-1 min-w-0">
            {content.tipTitle && (
              <p className="text-sm font-bold text-content-primary mb-1">{content.tipTitle}</p>
            )}
            <p className="text-sm text-content-secondary leading-relaxed">{content.tipBody}</p>
          </div>
        </div>

        <p className="text-xs text-content-muted text-center">
          Você pode rever este tutorial em <b className="text-content-secondary">Meu Perfil</b>.
        </p>

        {/* Ações */}
        <div className="flex gap-2 pt-2">
          <button
            onClick={onSkip}
            className="flex-1 py-3 rounded-xl text-content-secondary font-medium hover:bg-surface-hover transition-colors"
          >
            Pular
          </button>
          <button
            ref={ctaRef}
            onClick={onComplete}
            className="flex-1 py-3 rounded-xl bg-accent text-accent-contrast font-bold hover:bg-accent-hover transition-colors shadow-lg shadow-accent/20"
          >
            {content.ctaLabel ?? 'Começar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MobileWelcomeModal;
